import React, { useEffect, useState } from "react";
import { GridGenerator } from "@actinc/dls/components";
import {
  Delete as DeleteIcon,
  DragHorizontalVariant as DragHorizontalVariantIcon,
  Plus as PlusIcon,
} from "@actinc/dls/icons/";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  ControlledFormInput,
  ControlledFormSelect,
} from "../../../../components";

import { useDraggableInPortal } from "../../../../services/hooks";
import { onDragEnd } from "../../../../services/utils";

const GapMatchInteractionForm = ({
  index,
  insertInteractionElements,
  interaction,
  readonly,
}) => {
  const namePrefix = `item.interactions[${index}]`;
  // console.debug(
  //   "GapMatchInteractionForm - index %s, interaction %o",
  //   index,
  //   interaction,
  // );

  const renderDraggable = useDraggableInPortal();

  const { control, getValues, setValue, watch } = useFormContext();
  watch([`${namePrefix}.elements`, `${namePrefix}.targets`]);

  const {
    append: appendElement,
    fields: elements,
    move: moveElement,
    remove: removeElement,
    update: updateElement,
  } = useFieldArray({
    control,
    name: `${namePrefix}.elements`,
  });
  const {
    append: appendTarget,
    fields: targets,
    remove: removeTarget,
    update: updateTarget,
  } = useFieldArray({
    control,
    name: `${namePrefix}.targets`,
  });

  const [matchGroupOptions, setMatchGroupOptions] = useState();

  useEffect(() => {
    updateMatchGroupOptions()()();
  }, [elements, targets]);

  const addElement = () => {
    appendElement({
      content: "",
      identifier: `Element${elements.length + 1}`,
      matchGroup: null,
      matchMax: 0,
    });
  };
  const addTarget = () => {
    const identifier = `Target${targets.length + 1}`;
    appendTarget({
      correctResponse: "",
      identifier,
      matchGroup: null,
      width: 100,
    });
    insertInteractionElements({
      elements: [identifier],
      interaction,
      type: "Gap Match",
    });
  };
  const deleteElement = (i) => () => {
    let deleted = false;
    [...Array(elements.length)].map((_a, j) => {
      const identifier = `Element${j + 1}`;
      // If we haven't reached the deleted element
      if (!deleted) {
        // If we are deleting the current index
        if (i === j) {
          // Remove this element from targets' correctResponse values
          getValues(`${namePrefix}.targets`).map((target, k) => {
            if (target.correctResponse === identifier) {
              updateTarget(k, {
                ...target,
                correctResponse: "",
              });
            }
          });
          deleted = true;
        }
        return;
      }
      // If we are past the deleted element, we need to change element identifiers and target correctResponse values
      replaceElementIdentifier(identifier)(`Element${j}`);
    });
    removeElement(i);
  };
  const deleteTarget = (i) => () => {
    // Get the item content according to the form and stick it in a div
    const content = getValues("item.content");
    const contentElement = window.document.createElement("div");
    contentElement.innerHTML = content;

    // Find all of the elements associated with the array and adjust them
    targets.map((target, j) => {
      if (j < i) return;
      const identifier = `Target${j}`;

      if (typeof jest === "undefined") {
        const el = contentElement.querySelector(
          `.interaction[data-interaction-name="${interaction.identifier}"][data-interaction-element="${target.identifier}"]`,
        );

        if (j === i) {
          el.remove();
          return;
        }

        el.setAttribute("data-interaction-element", identifier);
        el.textContent = el.textContent.replace(target.identifier, identifier);
      }
      updateTarget(j, { ...target, identifier });
    });
    removeTarget(i);

    // Save content changes and remove the interaction from the FieldArray
    setValue("item.content", contentElement.innerHTML);
  };
  const replaceElementIdentifier = (oldIdentifier) => (identifier) => {
    getValues(`${namePrefix}.targets`).map((target, k) => {
      if (target.correctResponse === oldIdentifier) {
        updateTarget(k, {
          ...target,
          correctResponse: identifier,
        });
      }
    });
    getValues(`${namePrefix}.elements`).map((element, k) => {
      if (element.identifier === oldIdentifier) {
        updateElement(k, {
          ...element,
          identifier,
        });
      }
    });
  };
  const updateCorrectResponseOptions = (i) => (e) => {
    const element = elements[i];
    updateElement(i, { ...element, content: e.target.value });
  };
  const updateMatchGroupOptions = (type) => (i) => (e) => {
    const set = new Set([...elements, ...targets].map((o) => o.matchGroup));
    set.add(e?.target.value);
    const matchGroupsToDisplay = Array.from(set).filter(Boolean).length + 1;
    setMatchGroupOptions(
      [...Array(matchGroupsToDisplay)]
        .map((_h, j) => `Match Group ${j + 1}`)
        .map((label) => ({ label, value: label })),
    );
    if (type && typeof i !== undefined) {
      switch (type) {
        case "element":
          const element = elements[i];
          updateElement(i, { ...element, matchGroup: e.target.value });
          break;
        case "target":
          const target = targets[i];
          updateTarget(i, { ...target, matchGroup: e.target.value });
          break;
      }
    }
  };

  if (!getValues(`${namePrefix}.identifier`)) return null;

  return (
    <Box
      sx={{
        alignItems: "self-start",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      }}
    >
      <Typography variant="h4">Elements</Typography>
      <DragDropContext onDragEnd={onDragEnd(moveElement)}>
        <Droppable droppableId={`${interaction.identifier}-elements`}>
          {(droppableProvided) => (
            <List
              dense={true}
              disablePadding={true}
              ref={droppableProvided.innerRef}
              style={{ width: "100%" }}
              {...droppableProvided.droppableProps}
            >
              {elements.map((element, i) => {
                const { id } = element;
                return (
                  <Draggable
                    draggableId={id}
                    index={i}
                    isDragDisabled={readonly}
                    key={id}
                  >
                    {renderDraggable((draggableProvided) => (
                      <ListItem
                        dense={true}
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <ListItemIcon>
                          <Box {...draggableProvided.dragHandleProps}>
                            <DragHorizontalVariantIcon />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          disableTypography={true}
                          primary={
                            <Typography
                              sx={{ paddingBottom: 4 }}
                              variant="subtitle1"
                            >
                              {element.identifier}
                            </Typography>
                          }
                          secondary={
                            <GridGenerator
                              groups={[
                                [
                                  {
                                    children: (
                                      <ControlledFormInput
                                        control={control}
                                        defaultValue={element.content}
                                        fullWidth={true}
                                        id={`${namePrefix}.elements[${i}].content`}
                                        label="Content"
                                        multiline={false}
                                        name={`${namePrefix}.elements[${i}].content`}
                                        onBlur={updateCorrectResponseOptions(i)}
                                        readonly={readonly}
                                        rules={{
                                          required: true,
                                        }}
                                      />
                                    ),
                                    key: `element-${id}-content`,
                                  },
                                  {
                                    children: (
                                      <ControlledFormSelect
                                        control={control}
                                        defaultValue={element.matchGroup}
                                        fullWidth={true}
                                        id={`${namePrefix}.elements[${i}].matchGroup`}
                                        label="Match Group"
                                        name={`${namePrefix}.elements[${i}].matchGroup`}
                                        onChange={updateMatchGroupOptions(
                                          "element",
                                        )(i)}
                                        options={matchGroupOptions}
                                        readonly={readonly}
                                      />
                                    ),
                                    key: `element-${id}-matchGroup`,
                                  },
                                  {
                                    children: (
                                      <ControlledFormSelect
                                        control={control}
                                        defaultValue={element.matchMax}
                                        fullWidth={true}
                                        id={`${namePrefix}.elements[${i}].matchMax`}
                                        label="Match Max"
                                        name={`${namePrefix}.elements[${i}].matchMax`}
                                        options={
                                          targets?.length
                                            ? [
                                                ...Array(targets.length + 1),
                                              ].map((_h, j) => ({
                                                label: j.toString(),
                                                value: j,
                                              }))
                                            : []
                                        }
                                        readonly={readonly}
                                        rules={{
                                          required: true,
                                        }}
                                      />
                                    ),
                                    key: `element-${id}-matchMax`,
                                  },
                                ],
                              ]}
                            />
                          }
                          sx={{
                            border: "1px solid gray",
                            borderRadius: 3,
                            padding: 1,
                            width: "100%",
                            "& .MuiGrid-item": { paddingTop: 2 },
                          }}
                        />
                        <ListItemSecondaryAction>
                          {!readonly && (
                            <Tooltip title="Delete Element">
                              <IconButton
                                disabled={elements.length <= 1}
                                edge="end"
                                onClick={deleteElement(i)}
                                size="large"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </Draggable>
                );
              })}
              {droppableProvided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      {!readonly && (
        <Button
          onClick={addElement}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Element
        </Button>
      )}
      <Typography variant="h4">Targets</Typography>
      <List dense={true} disablePadding={true} style={{ width: "100%" }}>
        {targets.map((target, i) => {
          const { id } = target;
          return (
            <ListItem dense={true} key={id}>
              <ListItemText
                disableTypography={true}
                primary={
                  <Typography sx={{ paddingBottom: 4 }} variant="subtitle1">
                    {target.identifier}
                  </Typography>
                }
                secondary={
                  <GridGenerator
                    groups={[
                      [
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              defaultValue={target.correctResponse}
                              fullWidth={true}
                              id={`${namePrefix}.targets[${i}].correctResponse`}
                              label="Correct Response"
                              name={`${namePrefix}.targets[${i}].correctResponse`}
                              options={elements
                                .filter((element) => {
                                  const value = target.matchGroup;
                                  return (
                                    (!value && !element.matchGroup) ||
                                    element.matchGroup === value
                                  );
                                })
                                .map(({ content, identifier }) => ({
                                  label: content,
                                  value: identifier,
                                }))}
                              readonly={readonly}
                              rules={{
                                required: true,
                              }}
                            />
                          ),
                          key: `target-${id}-correctResponse`,
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              defaultValue={target.matchGroup}
                              fullWidth={true}
                              id={`${namePrefix}.targets[${i}].matchGroup`}
                              label="Match Group"
                              name={`${namePrefix}.targets[${i}].matchGroup`}
                              onChange={updateMatchGroupOptions("target")(i)}
                              options={matchGroupOptions}
                              readonly={readonly}
                            />
                          ),
                          key: `target-${id}-matchGroup`,
                        },
                        {
                          children: (
                            <ControlledFormInput
                              control={control}
                              defaultValue={target.width}
                              fullWidth={true}
                              id={`${namePrefix}.targets[${i}].width`}
                              label="Width"
                              multiline={false}
                              name={`${namePrefix}.targets[${i}].width`}
                              readonly={readonly}
                              rules={{
                                maxLength: 3,
                                pattern: {
                                  message:
                                    "This field must contain a valid number",
                                  value: /^\d+$/,
                                },
                                required: true,
                              }}
                            />
                          ),
                          key: `target-${id}-width`,
                        },
                      ],
                    ]}
                  />
                }
                sx={{
                  border: "1px solid gray",
                  borderRadius: 3,
                  padding: 1,
                  width: "100%",
                  "& .MuiGrid-item": { paddingTop: 2 },
                }}
              />
              <ListItemSecondaryAction>
                {!readonly && (
                  <Tooltip title="Delete Target">
                    <IconButton
                      disabled={targets.length <= 1}
                      edge="end"
                      onClick={deleteTarget(i)}
                      size="large"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
      {!readonly && (
        <Button
          onClick={addTarget}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Target
        </Button>
      )}
    </Box>
  );
};

export { GapMatchInteractionForm };
